import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import { UploadSVG } from '../../../../svgComponents/uploadSVG';
import ConfirmationButton from '../../../ui/confirmationButton/ConfirmationButton';
import { LoadingSpinner } from '../../../ui/loadingSpinner';
import { Prompt } from '../../../ui/prompt';
import { DocumentTypeLabel } from '../chooseDocumentType/DocumentTypeLabel';
import { TDocument } from '../UploadDocumentsModal.reducer';
import { BANK_STATMENT_DOC_TYPE } from '../UploadDocumentsModalHelper';
import styles from './DocumentUploader.module.css';

const MAX_FILE_SIZE = 1073741274; // 1GB
const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'text/csv': ['.csv'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
        '.xls'
    ],
    'application/msword': ['.docx']
};

type TProps = {
    closeModal: () => void;
    goBackToDocumentTypeSelection: () => void;
    uploadDocument: (
        documents: File[],
        documentType: { label: string; value: string },
        feedbackIds?: number[] | null,
        applicationId?: string
    ) => void;
    documentType: { label: string; value: string };
    documentData: { numberOfBankStatementsInMonths?: number };
    documentsStatus: TDocument[];
    preventBackNavigation?: boolean;
    feedbackIds?: number[] | null;
    applicationId?: string;
};

export const DocumentStatus = ({ document }: { document: TDocument }) => {
    if (document.pending) {
        return (
            <div className={styles.pendingStyle} data-testid="pending">
                <div className={styles.documentName}>
                    <FormattedMessage
                        id="_.trading_data.document_uploader.uploading_document"
                        defaultMessage="Uploading {document}"
                        values={{ document: document.name }}
                    />
                </div>
                <LoadingSpinner className={styles.uploadingDocuments} />
            </div>
        );
    }

    if (document.success) {
        return (
            <div className={styles.successStyle} data-testid="success">
                <div className={styles.documentName}>
                    <FormattedMessage
                        id="_.trading_data.document_uploader.uploaded_document"
                        defaultMessage="Uploaded {document}"
                        values={{ document: document.name }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.errorStyle} data-testid="error">
            <div className={styles.documentName}>
                <FormattedMessage
                    id="_.trading_data.document_uploader.failed_document"
                    defaultMessage="Failed to upload {document}"
                    values={{ document: document.name }}
                />
            </div>
            <div className={styles.errorMessage}>
                {document.code === 'DuplicateDocumentError' ? (
                    <FormattedMessage
                        id="_.trading_data.document_uploader.failed_document_error.DuplicateDocument"
                        defaultMessage="The submitted document already exists"
                    />
                ) : (
                    <FormattedMessage
                        id="_.trading_data.document_uploader.failed_document_error.DocumentUpload"
                        defaultMessage="Document upload failed. Please try again later."
                    />
                )}
            </div>
        </div>
    );
};

export const DocumentUploader = ({
    closeModal,
    goBackToDocumentTypeSelection,
    uploadDocument,
    documentType,
    documentData: { numberOfBankStatementsInMonths },
    documentsStatus,
    preventBackNavigation,
    feedbackIds,
    applicationId
}: TProps) => {
    const onDrop = (acceptedFiles: File[]) => {
        uploadDocument(acceptedFiles, documentType, feedbackIds, applicationId);
    };

    return (
        <div data-testid="upload-bank-statements-modal">
            <div className={styles.title} data-testid="upload-document-title">
                <FormattedMessage
                    id="_.trading_data.document_uploader.title"
                    defaultMessage="Upload {documentType}"
                    values={{
                        documentType: (
                            <DocumentTypeLabel label={documentType.label} />
                        )
                    }}
                />
            </div>

            {_.isEqual(documentType, BANK_STATMENT_DOC_TYPE) && (
                <div>
                    <p data-testid="bank-statement-guidance">
                        {numberOfBankStatementsInMonths !== undefined ? (
                            <FormattedMessage
                                id="_.trading_data.document_uploader.bank_statements_for_months"
                                defaultMessage="We require all transactions from the past {months} months (from today)"
                                values={{
                                    months: numberOfBankStatementsInMonths
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="_.trading_data.document_uploader.bank_statement_guidance"
                                defaultMessage="It's important bank statements include transactions from the last 12 months up until as recently as possible - ideally last week."
                            />
                        )}
                    </p>
                </div>
            )}

            <div className={styles.uploadedFiles}>
                {documentsStatus.map((document, idx) => (
                    <DocumentStatus key={idx} document={document} />
                ))}
            </div>

            <Dropzone
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_FILE_SIZE}
                onDrop={onDrop}
            >
                {({ open, getRootProps, getInputProps }) => (
                    <div
                        className={styles.dropzone}
                        {...getRootProps({
                            onClick: (evt) => evt.stopPropagation()
                        })}
                    >
                        {/* eslint-disable-next-line iwoca/input-a11y */}
                        <input {...getInputProps()} />
                        <div className={styles.dropzoneContent}>
                            <UploadSVG />
                            <div className={styles.dropzoneMessage}>
                                <FormattedMessage
                                    id="_.trading_data.document_uploader.drag_and_drop"
                                    defaultMessage="Drag & drop file or return it via email to documents@iwoca.co.uk"
                                />
                            </div>
                            <button
                                className={styles.browseFile}
                                onClick={() => open()}
                            >
                                <FormattedMessage
                                    id="_.trading_data.document_uploader.browse_file"
                                    defaultMessage="Browse"
                                />
                            </button>
                            {numberOfBankStatementsInMonths !== undefined && (
                                <ul className="mx-auto my-xl max-w-xl list-disc p-m pl-xl text-left text-s font-reg">
                                    <li>
                                        <FormattedMessage
                                            id="_.trading_data.document_uploader.acceptance_info_one"
                                            defaultMessage="We accept PDF, JPG, JPEG and PNG files no more than 2GB."
                                        />
                                    </li>
                                    <li>
                                        <FormattedMessage
                                            id="_.trading_data.document_uploader.acceptance_info_two"
                                            defaultMessage="These should be for the account(s) where the majority of your business revenue is generated."
                                        />
                                    </li>
                                    <li>
                                        <FormattedMessage
                                            id="_.trading_data.document_uploader.acceptance_info_three"
                                            defaultMessage="Please ensure the statements display the business name, sort code, account number and the account balance."
                                        />
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </Dropzone>
            <Prompt className={styles.prompt}>
                {!preventBackNavigation && (
                    <button onClick={goBackToDocumentTypeSelection}>
                        <FormattedMessage
                            id="_.trading_data.document_uploader.back"
                            defaultMessage="Back"
                        />
                    </button>
                )}
                <ConfirmationButton onClick={closeModal}>
                    <FormattedMessage
                        id="_.trading_data.document_uploader.finish"
                        defaultMessage="Finish"
                    />
                </ConfirmationButton>
            </Prompt>
        </div>
    );
};
